import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    AlertDialog,
    Checkbox,
    DialogTrigger,
    Flex,
    TextArea,
    TextField,
    ColorPicker,
    View,
    ColorEditor,
    parseColor,
} from '@adobe/react-spectrum';
import './Settings.css';
import { Helmet } from 'react-helmet';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { BreadCrumbProfileNavBar } from '../breadcrumbs/buttons/BreadCrumbProfileNavBar';
import {
    disconnectPatreon,
    refreshPatreon,
    updateUserSettings,
    uploadAvatar,
} from '../../data/redux/slices/profileSlice';
import { getClientDomainPrefix } from '../../data/DynamoConnector';
import { GenericLoadingDialog } from '../genericLoadingDialog/GenericLoadingDialog';
import { UploadAvatar } from './crop/UploadAvatar';
import { AvatarImage } from '../avatarImage/AvatarImage';
import HeroPanel, { HeroPanelChip, HeroPanelTotal } from '../common/heroPanel/HeroPanel';

function SettingsCard({ eyebrow, title, hint, accent, children, className }) {
    const cls = ['settingsCard', accent && `settingsCard--${accent}`, className].filter(Boolean).join(' ');
    return (
        <section className={cls}>
            <header className="settingsCardHead">
                {eyebrow && <span className="settingsCardEyebrow">{eyebrow}</span>}
                {title && <h2 className="settingsCardTitle">{title}</h2>}
                {hint && <p className="settingsCardHint">{hint}</p>}
            </header>
            <div className="settingsCardBody">{children}</div>
        </section>
    );
}

function CharCounter({ value, max }) {
    const len = value?.length || 0;
    const close = len > max - 30;
    const over = len > max;
    return (
        <span className={`settingsCharCounter${close ? ' is-close' : ''}${over ? ' is-over' : ''}`} aria-live="polite">
            {len} / {max}
        </span>
    );
}

export function Settings() {
    const dispatch = useDispatch();

    /** selector **/
    const metadata = useSelector((state) => state?.profile?.metadata);
    const profilePreferences = useSelector((state) => state?.profile?.preferences?.profilePreferences);
    const importPreferences = useSelector((state) => state?.profile?.preferences?.importPreferences);
    const subscriberFeatures = useSelector((state) => state?.profile?.preferences?.subscriberFeatures);
    const isDiscordMod = useSelector((state) => state?.profile?.metadata?.isDiscordMod);
    const isFetching = useSelector((state) => state?.profile?.isFetching);
    const avatarUrl = useSelector((state) => state?.profile?.metadata?.avatarUri);

    /** state **/
    const [profileName, setProfileName] = useState(metadata?.profileName || '');
    const [bio, setBio] = useState(metadata?.bio || '');
    const [isPrivate, setIsPrivate] = useState(profilePreferences?.isPrivate || false);
    const [hideLinkedAccounts, setHideLinkedAccounts] = useState(profilePreferences?.hideLinkedAccounts || false);
    const [allowPublicImport, setAllowPublicImport] = useState(importPreferences?.allowPublicImport || false);
    const [hideAuthorInfo, setHideAuthorInfo] = useState(importPreferences?.hideAuthorInfo || false);
    const [markDecksAsPrivate, setMarkDecksAsPrivate] = useState(importPreferences?.markDecksAsPrivate || false);

    const subscriberPerksUnlocked = subscriberFeatures?.isSubscribed || isDiscordMod;

    const [showSubscriberLevelOnDeckRows, setShowSubscriberLevelOnDeckRows] = useState(
        subscriberPerksUnlocked ? subscriberFeatures?.perks?.showSubscriberLevelOnDeckRows : false
    );
    const [showSubscriberLevelOnProfile, setShowSubscriberLevelOnProfile] = useState(
        subscriberPerksUnlocked ? subscriberFeatures?.perks?.showSubscriberLevelOnProfile : false
    );
    const [useDeckRowHighlighting, setUseDeckRowHighlighting] = useState(
        subscriberPerksUnlocked ? subscriberFeatures?.perks?.useDeckRowHighlighting : false
    );
    const [deckRowHighlighting, setDeckRowHighlighting] = useState(
        subscriberFeatures?.perks?.deckRowHighlighting ? parseColor(subscriberFeatures?.perks?.deckRowHighlighting) : ''
    );

    const handleUpload = async (file) => {
        dispatch(uploadAvatar(file, file.size));
    };

    const handleSavePress = async () => {
        if (profileName && profileName !== '') {
            const hslValue = useDeckRowHighlighting && deckRowHighlighting ? deckRowHighlighting.toString() : null;
            dispatch(
                updateUserSettings({
                    profileName,
                    bio,
                    isPrivate,
                    hideLinkedAccounts,
                    allowPublicImport,
                    hideAuthorInfo,
                    markDecksAsPrivate,
                    showSubscriberLevelOnDeckRows,
                    showSubscriberLevelOnProfile,
                    useDeckRowHighlighting,
                    deckRowHighlighting: hslValue,
                })
            );
        }
    };

    /** helmet **/
    const helmetName = `Settings`;
    const helmetCanonical = `https://www.commandersalt.com/settings`;
    const helmetDescription = `The saltiest MtG deck builders on the planet!`;
    const helmetTitle = `Commandersalt.com - The saltiest MtG deck builders on the planet!`;

    const [imgSrc, setImgSrc] = useState('');
    const [showUploadDialog, setShowUploadDialog] = useState(false);

    const onSelectFile = async (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImgSrc(reader.result?.toString());
                setShowUploadDialog(true);
            });
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const onCloseUploadDialog = () => setShowUploadDialog(false);

    const loginToPatreon = async () => {
        window.location.href = `https://www.patreon.com/oauth2/authorize?response_type=code&client_id=0DRv84QK_AgskGxIT8ijnwHogq-RCCe-WIgpF73GyMD0gS3LZswFXEtkc9ndsMO6&redirect_uri=${getClientDomainPrefix()}/settings&scopes=identity`;
    };

    const tierLabel = subscriberFeatures?.subscriberTierName;
    const isPatreonLinked = subscriberFeatures?.patreonLink?.isLinked;
    const isPatreonMember = subscriberFeatures?.isMember;

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs content={<BreadCrumbProfileNavBar />} />
            {isFetching && <GenericLoadingDialog />}
            {showUploadDialog && <UploadAvatar imgSrc={imgSrc} onClose={onCloseUploadDialog} />}

            <div className="settingsPage">
                <HeroPanel
                    tone="gold"
                    className="settingsHero"
                    eyebrow="Profile Settings"
                    title="Customize your seasoning"
                    hint="Tune your profile copy, privacy, imports, and account perks. Saves are atomic — hit the button at the bottom when you're done."
                    aside={
                        <>
                            <HeroPanelTotal
                                label={isPatreonLinked ? 'Patreon' : 'Account'}
                                value={isPatreonMember ? tierLabel || 'Member' : isPatreonLinked ? 'Linked' : 'Free'}
                            />
                            {isDiscordMod && (
                                <HeroPanelChip accent="warn" label="Status" value="Friend of the silo ❤" />
                            )}
                        </>
                    }
                />

                <div className="settingsGrid">
                    <SettingsCard
                        eyebrow="Avatar & Identity"
                        title="How the pod sees you"
                        hint="Click the avatar to swap. 30 chars on the name, 700 on the bio."
                    >
                        <div className="settingsIdentityRow">
                            <div className="settingsAvatarSlot">
                                <button
                                    type="button"
                                    className="settingsAvatarBtn"
                                    onClick={() => document.getElementById('settings-avatar-input').click()}
                                    disabled={isFetching}
                                    aria-label="Change avatar"
                                >
                                    <AvatarImage size={160} avatarUrl={avatarUrl} showDefaultIcon={true} />
                                    <span className="settingsAvatarOverlay">
                                        <span className="settingsAvatarOverlayText">Click to change</span>
                                    </span>
                                </button>
                                <input
                                    id="settings-avatar-input"
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={onSelectFile}
                                />
                            </div>
                            <div className="settingsIdentityFields">
                                <div className="settingsField">
                                    <TextField
                                        label="Profile name"
                                        value={profileName}
                                        onChange={setProfileName}
                                        width="100%"
                                        maxLength={30}
                                        isDisabled={isFetching}
                                    />
                                    <CharCounter value={profileName} max={30} />
                                </div>
                                <div className="settingsField">
                                    <TextArea
                                        label="Bio"
                                        value={bio}
                                        onChange={setBio}
                                        width="100%"
                                        height={140}
                                        maxLength={700}
                                        isDisabled={isFetching}
                                    />
                                    <CharCounter value={bio} max={700} />
                                </div>
                            </div>
                        </div>
                    </SettingsCard>

                    <SettingsCard
                        eyebrow="Patreon Connection"
                        title="Account perks"
                        hint={
                            isPatreonMember
                                ? 'Subscription perks unlocked while your Patreon stays linked.'
                                : 'Linking your Patreon is the only way to flip on subscriber perks.'
                        }
                        accent={isPatreonMember ? 'gold' : isPatreonLinked ? 'neutral' : 'red'}
                    >
                        {!isPatreonLinked ? (
                            <div className="settingsPatreonRow">
                                <p className="settingsPatreonStatus">
                                    Not linked. Click below to bounce over to Patreon and authorize.
                                </p>
                                <button
                                    type="button"
                                    className="searedPrimaryBtn"
                                    onClick={loginToPatreon}
                                    disabled={isFetching}
                                >
                                    Connect Patreon
                                </button>
                            </div>
                        ) : (
                            <div className="settingsPatreonRow">
                                <div className="settingsPatreonState">
                                    <span className="settingsPatreonBadge">
                                        <span className="settingsPatreonDot" />
                                        Patreon linked
                                    </span>
                                    {isPatreonMember ? (
                                        <span className="settingsPatreonTier">
                                            <span className="settingsPatreonTierLabel">Tier</span>
                                            <span className="settingsPatreonTierValue">{tierLabel || 'Member'}</span>
                                        </span>
                                    ) : (
                                        <span className="settingsPatreonTier settingsPatreonTier--free">
                                            <span className="settingsPatreonTierLabel">Status</span>
                                            <span className="settingsPatreonTierValue">Not a member</span>
                                        </span>
                                    )}
                                </div>
                                <div className="settingsPatreonActions">
                                    {!isPatreonMember && (
                                        <>
                                            <button
                                                type="button"
                                                className="searedPrimaryBtn"
                                                onClick={() =>
                                                    window.open(`https://www.patreon.com/c/user?u=4796225`, `_blank`)
                                                }
                                                disabled={isFetching}
                                            >
                                                Join the Patreon
                                            </button>
                                            <button
                                                type="button"
                                                className="settingsBtn"
                                                onClick={() => dispatch(refreshPatreon())}
                                                disabled={isFetching}
                                            >
                                                Refresh
                                            </button>
                                        </>
                                    )}
                                    <DialogTrigger>
                                        <button
                                            type="button"
                                            className="settingsBtn settingsBtn--danger"
                                            disabled={isFetching}
                                        >
                                            Disconnect
                                        </button>
                                        <AlertDialog
                                            variant="destructive"
                                            title="Confirm Disconnect"
                                            primaryActionLabel="Disconnect"
                                            cancelLabel="Cancel"
                                            onPrimaryAction={() => dispatch(disconnectPatreon())}
                                        >
                                            Are you sure you want to disconnect your Patreon? You'll lose subscriber
                                            perks immediately.
                                        </AlertDialog>
                                    </DialogTrigger>
                                </div>
                            </div>
                        )}
                    </SettingsCard>

                    <SettingsCard
                        eyebrow="Privacy & Display"
                        title="Who sees what"
                        hint="Privacy options apply across your profile, leaderboard, and search appearances."
                    >
                        <div className="settingsCheckGrid">
                            <Checkbox isSelected={isPrivate} onChange={setIsPrivate} isDisabled={isFetching}>
                                Profile is private
                            </Checkbox>
                            <Checkbox
                                isSelected={hideLinkedAccounts}
                                onChange={setHideLinkedAccounts}
                                isDisabled={isFetching}
                            >
                                Hide linked accounts on profile
                            </Checkbox>
                            <Checkbox
                                isDisabled={!subscriberPerksUnlocked || isFetching}
                                isSelected={showSubscriberLevelOnProfile}
                                onChange={setShowSubscriberLevelOnProfile}
                            >
                                <span>
                                    Display subscriber tier on your profile{' '}
                                    {!subscriberPerksUnlocked && (
                                        <span className="settingsLockBadge">subscriber only</span>
                                    )}
                                </span>
                            </Checkbox>
                            <Checkbox
                                isDisabled={!subscriberPerksUnlocked || isFetching}
                                isSelected={showSubscriberLevelOnDeckRows}
                                onChange={setShowSubscriberLevelOnDeckRows}
                            >
                                <span>
                                    Display subscriber tier on deck rows in search & leaderboards{' '}
                                    {!subscriberPerksUnlocked && (
                                        <span className="settingsLockBadge">subscriber only</span>
                                    )}
                                </span>
                            </Checkbox>
                        </div>
                    </SettingsCard>

                    <SettingsCard
                        eyebrow="Import Preferences"
                        title="Defaults for new decks"
                        hint="These flip the default for every fresh import. You can still override per deck on Manage Decks."
                    >
                        <div className="settingsCheckGrid">
                            <Checkbox
                                isSelected={allowPublicImport}
                                onChange={setAllowPublicImport}
                                isDisabled={isFetching}
                            >
                                Allow anyone to import your decks
                            </Checkbox>
                            <Checkbox isSelected={hideAuthorInfo} onChange={setHideAuthorInfo} isDisabled={isFetching}>
                                Hide author info on imported decks
                            </Checkbox>
                            <Checkbox
                                isSelected={markDecksAsPrivate}
                                onChange={setMarkDecksAsPrivate}
                                isDisabled={isFetching}
                            >
                                Mark imported decks private by default
                            </Checkbox>
                        </div>
                    </SettingsCard>

                    <SettingsCard
                        eyebrow="Subscriber perks"
                        title="Cosmetics"
                        hint="Visual flair unlocked while you're a member."
                        accent={subscriberPerksUnlocked ? 'gold' : 'neutral'}
                    >
                        <div className="settingsHighlightRow">
                            <Checkbox
                                isDisabled={!subscriberPerksUnlocked || isFetching}
                                isSelected={useDeckRowHighlighting}
                                onChange={setUseDeckRowHighlighting}
                            >
                                <span>
                                    Highlight your decks in list views{' '}
                                    {!subscriberPerksUnlocked && (
                                        <span className="settingsLockBadge">subscriber only</span>
                                    )}
                                </span>
                            </Checkbox>
                            <View isHidden={!useDeckRowHighlighting}>
                                <ColorPicker
                                    label="Highlight color"
                                    value={deckRowHighlighting}
                                    onChange={setDeckRowHighlighting}
                                    isDisabled={isFetching}
                                >
                                    <ColorEditor />
                                </ColorPicker>
                            </View>
                        </div>
                    </SettingsCard>
                </div>

                <div className="settingsSaveBar">
                    <span className="settingsSaveHint">Changes apply immediately on save.</span>
                    <button
                        type="button"
                        className="searedPrimaryBtn searedPrimaryBtn--lg"
                        disabled={isFetching || !profileName}
                        onClick={handleSavePress}
                    >
                        Save Settings
                    </button>
                </div>
            </div>
        </Flex>
    );
}
